import { useEffect, useState } from "react";
import { adminStats, type SlotStat } from "../../lib/api";
import { useI18n } from "../../i18n";
import { localizedDate } from "../../../shared/dates";
import { Gift } from "../Icon";

export function StatsTab() {
  const { t, lang } = useI18n();
  const [data, setData] = useState<{ slots: SlotStat[]; totals: { bookings: number; guests: number } } | null>(null);

  useEffect(() => {
    adminStats().then(setData).catch(() => {});
  }, []);

  if (!data) return <p className="text-ink/50">{t("common.loading")}</p>;

  // Group slots by day for a responsive grid per day.
  const byDay = new Map<string, SlotStat[]>();
  for (const s of data.slots) {
    const arr = byDay.get(s.event_date) ?? [];
    arr.push(s);
    byDay.set(s.event_date, arr);
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-4 sm:max-w-md">
        <div className="card text-center">
          <div className="text-3xl font-bold text-[var(--theme-deep)]">{data.totals.bookings}</div>
          <div className="text-sm text-ink/60">{t("admin.totalBookings")}</div>
        </div>
        <div className="card text-center">
          <div className="text-3xl font-bold text-[var(--theme-deep)]">{data.totals.guests}</div>
          <div className="text-sm text-ink/60">{t("admin.totalGuests")}</div>
        </div>
      </div>

      {[...byDay.entries()].map(([date, slots]) => (
        <div key={date} className="mt-7">
          <h3 className="mb-2 font-display font-bold text-ink">{localizedDate(date, lang)}</h3>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {slots.map((s) => {
              const pct = Math.round((s.booked / s.capacity) * 100);
              const full = s.remaining <= 0;
              return (
                <div key={s.id} className="rounded-2xl bg-white p-4 ring-1 ring-black/[0.06]">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{s.start_time}–{s.end_time}</span>
                    <span className={`text-sm font-semibold ${full ? "text-red-500" : "text-ink/60"}`}>
                      {s.booked}/{s.capacity}
                    </span>
                  </div>
                  <div className="mt-2.5 h-2.5 w-full overflow-hidden rounded-full bg-black/5">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${pct}%`, background: full ? "#ef4444" : "var(--theme-main)" }}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-ink/55">
                    <span>{t("admin.remaining")} {s.remaining} · {s.bookings} {t("admin.bookings")}</span>
                    <span className="inline-flex items-center gap-1">
                      <Gift className="h-3.5 w-3.5" /> {s.giveaway_quota}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
